import React, { useState, useEffect, useContext, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { ToastContext } from './ToastProvider';
import useCoopMemberRole from '../hooks/useCoopMemberRole';
import useAdminPasswordAccess from '../hooks/useAdminPasswordAccess';
import useUserProfile from '../hooks/useUserProfile';
import DeliveryDetailsModal from './DeliveryDetailsModal';
import RemittanceConfirmationModal from './RemittanceConfirmationModal';

const DeliveryManagementDashboard = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [pickupOrders, setPickupOrders] = useState([]);
  const [completedPickupOrders, setCompletedPickupOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showRemittanceModal, setShowRemittanceModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('deliveries'); // 'deliveries' | 'pickup'
  const [pickupView, setPickupView] = useState('ready'); // 'ready' | 'completed'
  const [deliverySettings, setDeliverySettings] = useState(null);
  const [storeInfo, setStoreInfo] = useState({});
  
  const { notify } = useContext(ToastContext);
  const { hasAnyRole, coopRole, loading: roleLoading } = useCoopMemberRole(true);
  const { hasAdminAccess } = useAdminPasswordAccess();
  const { profile } = useUserProfile();

  // Check if user has access to this dashboard
  // Allow cashier, treasurer, admin, OR delivery role users
  const canAccess = (coopRole && ['cashier', 'treasurer'].includes(coopRole)) || 
                   hasAdminAccess || 
                   hasAnyRole(['delivery', 'rider']);

  // Check if user is delivery role (for filtering orders)
  const isDeliveryUser = hasAnyRole(['delivery', 'rider']);

  // Function to fetch store information
  const fetchStoreInfo = async (storeType, storeId) => {
    if (!storeType || !storeId) return null;
    
    try {
      // First try to get store by actual document ID
      const storeDoc = await getDoc(doc(db, 'stores', storeId));
      if (storeDoc.exists()) {
        return { id: storeDoc.id, ...storeDoc.data() };
      }
      
      // If not found by ID and it's cooperative, try to find by type
      if (storeType === 'cooperative') {
        const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
        const storesQuery = query(
          collection(db, 'stores'),
          where('type', '==', 'cooperative'),
          limit(1)
        );
        const storesSnap = await getDocs(storesQuery);
        if (!storesSnap.empty) {
          const storeData = storesSnap.docs[0].data();
          return { id: storesSnap.docs[0].id, ...storeData };
        }
      }
    } catch (error) {
      console.warn('Error fetching store info:', error);
    }
    return null;
  };

  // Additional Rider profiles fetched from Firestore when missing
  const [riderProfiles, setRiderProfiles] = useState({});

  // Create riders lookup map (support multiple id keys + merge fetched profiles)
  const ridersByUid = React.useMemo(() => {
    const map = Object.create(null);
    (riders || []).forEach((rider) => {
      if (!rider) return;
      const candidateIds = [rider.uid, rider.id, rider.userId, rider.riderUid, rider.riderId];
      candidateIds.filter(Boolean).forEach((key) => { map[key] = rider; });
    });

    // Merge any fetched profiles
    Object.entries(riderProfiles || {}).forEach(([uid, prof]) => {
      map[uid] = { ...(map[uid] || {}), ...prof };
    });


    return map;
  }, [riders, riderProfiles]);

  // Opportunistically fetch missing rider names from Firestore users collection
  useEffect(() => {
    const allIds = Array.from(new Set((deliveries || []).map(d => d?.riderId).filter(Boolean)));
    const missing = allIds.filter((id) => !(id in ridersByUid) && !(id in (riderProfiles || {})));
    if (missing.length === 0) return;

    (async () => {
      try {
        const { collection, getDocs, query, where, documentId } = await import('firebase/firestore');
        const results = {};
        for (let i = 0; i < missing.length; i += 10) {
          const batch = missing.slice(i, i + 10);
          // Prefer querying by documentId (usually uid)
          const q = query(collection(db, 'users'), where(documentId(), 'in', batch));
          const snap = await getDocs(q);
          snap.forEach((doc) => {
            const data = doc.data() || {};
            const uid = data.uid || doc.id;
            results[uid] = {
              uid,
              name: data.name || data.displayName || data.fullName || data.email || `Rider ${uid.slice(0,8)}...`,
              displayName: data.displayName,
              email: data.email,
              phone: data.phone,
            };
          });
        }
        if (Object.keys(results).length > 0) {
          setRiderProfiles((prev) => ({ ...prev, ...results }));
        }
      } catch (e) {
        console.warn('Unable to fetch rider user profiles:', e);
      }
    })();
  }, [deliveries, ridersByUid, riderProfiles]);

  // Function to get rider name from riderId
  const getRiderName = (riderId) => {
    if (!riderId) {
      return 'Unassigned';
    }
    // Lookup from combined map first
    const rider = ridersByUid[riderId] || riderProfiles[riderId];
    if (!rider) {
      return `Rider ${riderId.slice(0, 8)}...`;
    }
    
    // Try multiple possible name fields
    const name = rider.name || rider.displayName || rider.riderName || rider.fullName || rider.email;
    if (name && name !== riderId) {
      return name;
    }
    
    // Fallback to showing partial ID
    return `Rider ${riderId.slice(0, 8)}...`;
  };

  // Fetch delivery settings
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "delivery"), (s) =>
      setDeliverySettings(s.data() || null)
    );
    return () => unsub();
  }, []);

  // Calculate delivery fee using same logic as Orders page
  const calculateDeliveryFee = useCallback((order) => {
    // Only apply delivery fee for delivery orders
    const isDeliveryOrder = order.method === "cod" || 
                           (order.method === "manual" && order.deliveryMode === "delivery") ||
                           order.deliveryMode === "delivery";
    
    if (!isDeliveryOrder) return 0;
    
    // Get delivery settings from database - handle both nested and flat structures
    const deliveryConfig = deliverySettings?.settings || deliverySettings || {};
    
    // Check if delivery is enabled
    if (deliveryConfig.enabled === false) return 0;
    
    // Check free delivery threshold - use the actual setting from database
    const freeThreshold = deliveryConfig.freeDeliveryThreshold;
    const subtotal = order.subtotal || 0;
    
    if (freeThreshold && subtotal >= freeThreshold) {
      return 0;
    }
    
    // Calculate fee based on delivery fee type
    if (deliveryConfig.deliveryFeeType === 'fixed') {
      const fee = deliveryConfig.fixedFee;
      if (fee && fee > 0) {
        return fee;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'percentage') {
      const fee = deliveryConfig.percentageFee;
      if (fee && fee > 0) {
        return (subtotal * fee) / 100;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'distance') {
      // For distance-based, use base fee for now
      const fee = deliveryConfig.baseFee;
      if (fee && fee > 0) {
        return fee;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'zones') {
      // For zones-based delivery, calculate based on delivery zones
      if (!deliveryConfig.deliveryZones || !Array.isArray(deliveryConfig.deliveryZones)) {
        const fee = deliveryConfig.baseFee;
        if (fee && fee > 0) {
          return fee;
        }
        return 0;
      }
      
      // For now, use the first zone fee as default
      const defaultZoneFee = deliveryConfig.deliveryZones[0]?.fee;
      if (defaultZoneFee && defaultZoneFee > 0) {
        return defaultZoneFee;
      } else {
        const fee = deliveryConfig.baseFee;
        if (fee && fee > 0) {
          return fee;
        }
        return 0;
      }
    }
    // No delivery fee type configured
    return 0;
  }, [deliverySettings]);

  // Calculate weight-based fee using same logic as Orders page
  const calculateWeightBasedFee = useCallback((order) => {
    if (!deliverySettings) return 0;
    // Only apply weight fees for delivery orders (COD or prepaid). Explicitly exclude pickup.
    const method = String(order.method || '').toLowerCase();
    const deliveryMode = String(order.deliveryMode || '').toLowerCase();
    const isDeliveryOrder = (method === 'cod' || method === 'prepaid') || deliveryMode === 'delivery';
    if (method === 'pickup' || deliveryMode === 'pickup') return 0;
    if (!isDeliveryOrder) return 0;

    const deliveryConfig = deliverySettings?.settings || deliverySettings || {};
    if (!deliveryConfig.weightBasedFees?.enabled) return 0;

    const orderWeight = order.orderWeight || 0;
    if (orderWeight <= 0) return 0;

    const { overweightThreshold, overweightFee } = deliveryConfig.weightBasedFees;
    if (!overweightThreshold || !overweightFee) return 0;

    if (orderWeight > overweightThreshold) {
      const overweight = orderWeight - overweightThreshold;
      return overweight * overweightFee;
    }

    return 0;
  }, [deliverySettings]);

  // Fetch riders
  useEffect(() => {
    async function loadRiders() {
      try {
        const fn = httpsCallable(functions, "listRiders");
        const res = await fn();
        const ridersData = Array.isArray(res?.data?.riders) ? res.data.riders : [];
        setRiders(ridersData);
      } catch (error) {
        console.error('Error loading riders:', error);
      }
    }
    
    if (canAccess) {
      loadRiders();
    }
  }, [canAccess]);

  // Fetch deliveries
  useEffect(() => {
    if (!canAccess) return;

    let deliveriesQuery;
    
    if (isDeliveryUser) {
      // Delivery users only see orders assigned to them
      deliveriesQuery = query(
        collection(db, 'deliveries'),
        where('riderId', '==', profile?.uid || profile?.id),
        orderBy('createdAt', 'desc')
      );
    } else {
      // Admin/cashier/treasurer see all orders
      deliveriesQuery = query(
        collection(db, 'deliveries'),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(deliveriesQuery, async (snapshot) => {
      const deliveryData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Enhance delivery data with customer information
      const enhancedDeliveries = await Promise.all(
        deliveryData.map(async (delivery) => {
          try {
            let customerName = 'Unknown';
            let customerEmail = delivery.buyerName || 'N/A';
            let customerPhone = delivery.phone || 'N/A';
            
            // First try to get customer name from users collection if we have userId
            if (delivery.userId) {
              const userDoc = await getDoc(doc(db, 'users', delivery.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                customerName = userData.firstName && userData.lastName 
                  ? `${userData.firstName} ${userData.lastName}`.trim()
                  : userData.displayName || userData.email || delivery.buyerName || 'Unknown';
                customerEmail = userData.email || delivery.buyerName || 'N/A';
                customerPhone = userData.phone || userData.phoneNumber || delivery.phone || 'N/A';
              }
            }
            // If no userId, try to get customer info from the order (only if user has permissions)
            else if (delivery.orderId && (hasAdminAccess || hasAnyRole(['delivery', 'rider', 'cashier', 'treasurer', 'packer']))) {
      
              try {
                const orderDoc = await getDoc(doc(db, 'orders', delivery.orderId));
                if (orderDoc.exists()) {
                  const orderData = orderDoc.data();
                  if (orderData.userId) {
                    const userDoc = await getDoc(doc(db, 'users', orderData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      customerName = userData.firstName && userData.lastName 
                        ? `${userData.firstName} ${userData.lastName}`.trim()
                        : userData.displayName || userData.email || delivery.buyerName || 'Unknown';
                      customerEmail = userData.email || delivery.buyerName || 'N/A';
                      customerPhone = userData.phone || userData.phoneNumber || delivery.phone || 'N/A';
                    }
                  }
                }
              } catch (orderError) {
                console.warn('Error fetching order data for delivery:', delivery.id, orderError);
                // Don't let this error affect the delivery data - continue with fallback
              }
            }
            
            // If we still don't have a proper name, try to extract it from buyerName
            if (customerName === 'Unknown' && delivery.buyerName) {
              // Check if buyerName looks like an email
              if (delivery.buyerName.includes('@')) {
                // It's an email, try to extract name from it
                const emailParts = delivery.buyerName.split('@')[0];
                // Convert email username to a more readable format
                customerName = emailParts
                  .replace(/[._-]/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              } else {
                // Not an email, use as is
                customerName = delivery.buyerName;
              }
            }
            
            // Fetch store information if we have order data
            let storeData = null;
            if (delivery.orderId) {
              try {
                const orderDoc = await getDoc(doc(db, 'orders', delivery.orderId));
                if (orderDoc.exists()) {
                  const orderData = orderDoc.data();
                  if (orderData.storeType && orderData.storeId) {
                    storeData = await fetchStoreInfo(orderData.storeType, orderData.storeId);
                  }
                }
              } catch (storeError) {
                console.warn('Error fetching store data for delivery:', delivery.id, storeError);
              }
            }
            
            return {
              ...delivery,
              customerName,
              customerEmail,
              customerPhone,
              storeData
            };
          } catch (error) {
            console.warn('Error fetching customer data for delivery:', delivery.id, error);
            // Fallback: try to extract name from buyerName if it's an email
            let fallbackName = delivery.buyerName || 'Unknown';
            if (fallbackName !== 'Unknown' && fallbackName.includes('@')) {
              const emailParts = fallbackName.split('@')[0];
              fallbackName = emailParts
                .replace(/[._-]/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            }
            
            // Fetch store information if we have order data (fallback case)
            let storeData = null;
            if (delivery.orderId) {
              try {
                const orderDoc = await getDoc(doc(db, 'orders', delivery.orderId));
                if (orderDoc.exists()) {
                  const orderData = orderDoc.data();
                  if (orderData.storeType && orderData.storeId) {
                    storeData = await fetchStoreInfo(orderData.storeType, orderData.storeId);
                  }
                }
              } catch (storeError) {
                console.warn('Error fetching store data for delivery (fallback):', delivery.id, storeError);
              }
            }
            
            return {
              ...delivery,
              customerName: fallbackName,
              customerEmail: delivery.buyerName || 'N/A',
              customerPhone: delivery.phone || 'N/A',
              storeData
            };
          }
        })
      );
      
      // For delivery users, also filter to show only ready orders if they want
      let filteredData = enhancedDeliveries;
      if (isDeliveryUser && filterStatus === 'ready') {
        filteredData = enhancedDeliveries.filter(d => d.readyForPickup === true);
      }
      
      setDeliveries(filteredData);
      setLoading(false);
    }, (error) => {
              console.error('Error fetching deliveries:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [canAccess, isDeliveryUser, profile?.uid, profile?.id, filterStatus]);

  // Fetch pickup orders (orders with status "ready" and method === "pickup")
  useEffect(() => {
    if (!canAccess) return;

    const pickupQuery = query(
      collection(db, 'orders'),
      where('status', '==', 'ready'),
      where('method', '==', 'pickup'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(pickupQuery, (snapshot) => {
      const pickupData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPickupOrders(pickupData);
    }, (error) => {
              console.error('Error fetching pickup orders:', error);
    });

    return () => unsubscribe();
  }, [canAccess]);

  // Fetch completed pickup orders (orders with status "completed" and method === "pickup")
  useEffect(() => {
    if (!canAccess) return;

    const completedQuery = query(
      collection(db, 'orders'),
      where('status', '==', 'completed'),
      where('method', '==', 'pickup'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(completedQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCompletedPickupOrders(data);
    }, (error) => {
      console.error('Error fetching completed pickup orders:', error);
    });

    return () => unsubscribe();
  }, [canAccess]);

  const filteredDeliveries = deliveries.filter(delivery => {
    let matchesStatus = true;
    
    if (filterStatus === 'ready') {
      matchesStatus = delivery.readyForPickup === true;
    } else if (filterStatus === 'queued') {
      matchesStatus = delivery.status === 'queued';
    } else if (filterStatus !== 'all') {
      matchesStatus = delivery.status === filterStatus;
    }
    
    const matchesSearch = searchTerm === '' || 
                         delivery.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         delivery.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         delivery.riderName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Get counts for each status
  const statusCounts = {
    all: deliveries.length,
    ready: deliveries.filter(d => d.readyForPickup === true).length,
    queued: deliveries.filter(d => d.status === 'queued').length,
    assigned: deliveries.filter(d => d.status === 'assigned').length,
    out_for_delivery: deliveries.filter(d => d.status === 'out_for_delivery').length,
    for_review: deliveries.filter(d => d.status === 'for_review').length,
    remittance_pending: deliveries.filter(d => d.status === 'remittance_pending').length,
    handover_confirmed: deliveries.filter(d => d.status === 'handover_confirmed').length,
    completed: deliveries.filter(d => d.status === 'completed').length,
    cancelled: deliveries.filter(d => d.status === 'cancelled').length
  };



  const handleStatusUpdate = async (deliveryId, newStatus) => {
    try {
      await updateDoc(doc(db, 'deliveries', deliveryId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        lastStatusChange: serverTimestamp()
      });
      notify(`Delivery status updated to ${newStatus}`, { type: 'success' });
    } catch (error) {
      console.error('Error updating delivery status:', error);
      notify('Failed to update delivery status', { type: 'error' });
    }
  };

  const openRemittanceModal = (delivery) => {
    setSelectedDelivery(delivery);
    setShowRemittanceModal(true);
  };

  const openDeliveryModal = (delivery) => {
    setSelectedDelivery(delivery);
    setShowDeliveryModal(true);
  };

  const handleConfirmHandover = async (deliveryId) => {
    try {
      
      // Call the correct Cloud Function for completing deliveries after handover
      const completeDelivery = httpsCallable(functions, 'completeDeliveryAfterHandover');
      await completeDelivery({ 
        deliveryId, 
        note: 'Delivery completed after handover confirmation'
      });
      
      notify('Delivery completed successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error completing delivery:', error);
      console.error('Error details:', error.code, error.message);
      notify('Failed to complete delivery: ' + error.message, { type: 'error' });
    }
  };

  const handleCompletePickupOrder = async (orderId, notes = '') => {
    try {
      const completePickupOrder = httpsCallable(functions, 'completePickupOrder');
      await completePickupOrder({ 
        orderId, 
        notes 
      });
      
      notify('Pickup order completed successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error completing pickup order:', error);
      notify('Failed to complete pickup order: ' + error.message, { type: 'error' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'assigned': 'bg-blue-100 text-blue-800',
      'out_for_delivery': 'bg-purple-100 text-purple-800',
      'for_review': 'bg-orange-100 text-orange-800',
      'remittance_pending': 'bg-orange-100 text-orange-800',
      'handover_confirmed': 'bg-indigo-100 text-indigo-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getRemittanceStatus = (delivery) => {
    if (delivery.status === 'completed') {
      // Check if store wallet was credited
      if (delivery.storeData) {
        return 'Completed - Store Wallet Credited';
      }
      return 'Completed';
    }
    if (delivery.remittances && delivery.remittances.length > 0) {
      const totalRemitted = delivery.remittances.reduce((sum, r) => sum + Number(r.amount || 0), 0);
      const codAmount = Number(delivery.codAmount || 0);
      if (totalRemitted >= codAmount) {
        if (delivery.storeData) {
          return 'Ready for Confirmation - Store Wallet Pending';
        }
        return 'Ready for Confirmation';
      }
      return `${totalRemitted.toFixed(2)} / ${codAmount.toFixed(2)}`;
    }
    return 'No Remittances';
  };

  // Show loading while role is being determined
  if (roleLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading deliveries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Delivery Management Dashboard</h1>
              <p className="text-gray-600">Manage deliveries, confirm remittances, and update delivery status</p>
            </div>

          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('deliveries')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'deliveries'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Deliveries
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {deliveries.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('pickup')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'pickup'
                  ? 'bg-brand-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pickup Orders
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {pickupOrders.length + completedPickupOrders.length}
              </span>
            </button>
          </div>
        </div>

        {/* Status Tabs - Only show for deliveries tab */}
        {activeTab === 'deliveries' && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(statusCounts).map(([status, count]) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterStatus === status
                      ? 'bg-brand-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'All' : 
                   status === 'ready' ? 'Ready for Pickup' :
                   status === 'queued' ? 'Queued' :
                   status === 'out_for_delivery' ? 'Out for Delivery' :
                   status === 'for_review' ? 'For Review' :
                   status === 'remittance_pending' ? 'Remittance Pending' :
                   status === 'handover_confirmed' ? 'Handover Confirmed' :
                   status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    filterStatus === status
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search by Order ID, Customer, or Rider..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        )}

        {/* Pickup Orders Search - Only show for pickup tab */}
        {activeTab === 'pickup' && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            {/* Pickup sub-tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={()=>setPickupView('ready')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pickupView==='ready' ? 'bg-brand-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >Ready <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${pickupView==='ready'?'bg-white/20 text-white':'bg-gray-200 text-gray-700'}`}>{pickupOrders.length}</span></button>
              <button
                onClick={()=>setPickupView('completed')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pickupView==='completed' ? 'bg-brand-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >Completed <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${pickupView==='completed'?'bg-white/20 text-white':'bg-gray-200 text-gray-700'}`}>{completedPickupOrders.length}</span></button>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search pickup orders by Order ID or Customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        )}
        

        {/* Deliveries Table - Only show for deliveries tab */}
        {activeTab === 'deliveries' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Store
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      COD Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remittance Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeliveries.map((delivery) => (
                    <tr key={delivery.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {delivery.orderId || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{delivery.buyerName || 'Unknown'}</div>
                          <div className="text-gray-500 text-xs">{delivery.address || 'No address'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {delivery.storeData ? (
                          <div>
                            <div className="font-medium text-blue-700">{delivery.storeData.name || 'Unknown Store'}</div>
                            <div className="text-gray-500 text-xs">{delivery.storeData.type || 'Store'}</div>
                            {delivery.storeData.address && (
                              <div className="text-gray-500 text-xs">{delivery.storeData.address}</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-400 text-xs">No store info</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getRiderName(delivery.riderId)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(delivery.status)}`}>
                            {delivery.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                          </span>
                          {delivery.readyForPickup && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              READY FOR PICKUP
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₱{Number(delivery.codAmount || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getRemittanceStatus(delivery)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openDeliveryModal(delivery)}
                            className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md text-xs"
                          >
                            View Details
                          </button>
                          {delivery.status === 'remittance_pending' && delivery.remittances && delivery.remittances.length > 0 && (
                            <button
                              onClick={() => openRemittanceModal(delivery)}
                              className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md text-xs"
                            >
                              Confirm Remittance
                            </button>
                          )}
                          {delivery.status === 'handover_confirmed' && (
                            <button
                              onClick={() => handleConfirmHandover(delivery.id)}
                              className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md text-xs"
                            >
                              Complete Delivery
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden">
              {filteredDeliveries.map((delivery) => (
                <div key={delivery.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900">{delivery.orderId || 'N/A'}</h3>
                      <p className="text-sm text-gray-600">{delivery.buyerName || 'Unknown'}</p>
                      {delivery.address && (
                        <p className="text-xs text-gray-500">{delivery.address}</p>
                      )}
                    </div>
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(delivery.status)}`}>
                        {delivery.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                      </span>
                      {delivery.readyForPickup && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          READY FOR PICKUP
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                    <div>
                      <span className="text-gray-500">Rider:</span>
                      <span className="ml-1 text-gray-900">{getRiderName(delivery.riderId)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">COD:</span>
                      <span className="ml-1 text-gray-900">₱{Number(delivery.codAmount || 0).toFixed(2)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Store:</span>
                      <span className="ml-1 text-gray-900">
                        {delivery.storeData ? (
                          <span className="text-blue-700 font-medium">
                            {delivery.storeData.name || 'Unknown Store'} ({delivery.storeData.type || 'Store'})
                          </span>
                        ) : (
                          <span className="text-gray-400">No store info</span>
                        )}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Remittance:</span>
                      <span className="ml-1 text-gray-900">{getRemittanceStatus(delivery)}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openDeliveryModal(delivery)}
                      className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-100 transition-colors"
                    >
                      View Details
                    </button>
                    {delivery.status === 'remittance_pending' && delivery.remittances && delivery.remittances.length > 0 && (
                      <button
                        onClick={() => openRemittanceModal(delivery)}
                        className="flex-1 bg-green-50 text-green-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-green-100 transition-colors"
                      >
                        Confirm Remittance
                      </button>
                    )}
                    {delivery.status === 'handover_confirmed' && (
                      <button
                        onClick={() => handleConfirmHandover(delivery.id)}
                        className="flex-1 bg-indigo-50 text-indigo-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-indigo-100 transition-colors"
                      >
                        Complete Delivery
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pickup Orders Table - Only show for pickup tab */}
        {activeTab === 'pickup' && pickupView==='ready' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pickupOrders
                    .filter(order => 
                      searchTerm === '' || 
                      order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order.orderId || order.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{order.buyerName || 'Unknown'}</div>
                            <div className="text-gray-500 text-xs">{order.phone || 'No phone'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="capitalize">{order.deliveryMethod || 'pickup'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {deliverySettings ? 
                            `₱${Number((order.subtotal || 0) + calculateDeliveryFee(order) + calculateWeightBasedFee(order)).toFixed(2)}` : 
                            `₱${Number(order.grandTotal || order.total || 0).toFixed(2)}`
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            READY FOR PICKUP
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleCompletePickupOrder(order.id)}
                            className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md text-xs"
                          >
                            ✅ Mark Picked Up
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards for Pickup Orders */}
            <div className="lg:hidden">
              {pickupOrders
                .filter(order => 
                  searchTerm === '' || 
                  order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((order) => (
                  <div key={order.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">{order.orderId || order.id}</h3>
                        <p className="text-sm text-gray-600">{order.buyerName || 'Unknown'}</p>
                        {order.phone && (
                          <p className="text-xs text-gray-500">{order.phone}</p>
                        )}
                      </div>
                      <div className="flex flex-col space-y-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          READY FOR PICKUP
                        </span>
                        <span className="text-xs text-gray-500 capitalize">{order.deliveryMethod || 'pickup'}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <span className="text-gray-500">Total:</span>
                        <span className="ml-1 text-gray-900">
                          {deliverySettings ? 
                            `₱${Number((order.subtotal || 0) + calculateDeliveryFee(order) + calculateWeightBasedFee(order)).toFixed(2)}` : 
                            `₱${Number(order.grandTotal || order.total || 0).toFixed(2)}`
                          }
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Items:</span>
                        <span className="ml-1 text-gray-900">{order.items?.length || 0}</span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCompletePickupOrder(order.id)}
                        className="flex-1 bg-green-50 text-green-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-green-100 transition-colors"
                      >
                        ✅ Mark Picked Up
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {activeTab === 'pickup' && pickupView==='completed' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {completedPickupOrders
                    .filter(order => 
                      searchTerm === '' || 
                      order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.orderId || order.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="font-medium">{order.buyerName || order.memberName || '—'}</div>
                          <div className="text-xs text-gray-500">{order.userId || order.memberUid || ''}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{Array.isArray(order.items) ? order.items.length : (order.itemCount || '—')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">{order.grandTotal !== undefined ? `₱${Number(order.grandTotal).toFixed(2)}` : (order.total !== undefined ? `₱${Number(order.total).toFixed(2)}` : '—')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{order.completedAt?.toDate ? new Date(order.completedAt.toDate()).toLocaleString() : (order.updatedAt?.toDate ? new Date(order.updatedAt.toDate()).toLocaleString() : '-')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="lg:hidden">
              {completedPickupOrders
                .filter(order => 
                  searchTerm === '' || 
                  order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((order) => (
                  <div key={order.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">{order.orderId || order.id}</h3>
                        <p className="text-sm text-gray-600">{order.buyerName || order.memberName || '—'}</p>
                        {order.phone && (
                          <p className="text-xs text-gray-500">{order.phone}</p>
                        )}
                      </div>
                      <div className="flex flex-col space-y-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          COMPLETED
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <span className="text-gray-500">Total:</span>
                        <span className="ml-1 text-gray-900">{order.grandTotal !== undefined ? `₱${Number(order.grandTotal).toFixed(2)}` : (order.total !== undefined ? `₱${Number(order.total).toFixed(2)}` : '—')}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Items:</span>
                        <span className="ml-1 text-gray-900">{Array.isArray(order.items) ? order.items.length : (order.itemCount || '—')}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Completed:</span>
                        <span className="ml-1 text-gray-900">{order.completedAt?.toDate ? new Date(order.completedAt.toDate()).toLocaleString() : (order.updatedAt?.toDate ? new Date(order.updatedAt.toDate()).toLocaleString() : '-')}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {activeTab === 'deliveries' && filteredDeliveries.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No deliveries found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Empty State for Pickup Orders */}
        {activeTab === 'pickup' && pickupView==='ready' && pickupOrders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No pickup orders found</h3>
            <p className="text-gray-500">All pickup orders have been completed or none are ready yet.</p>
          </div>
        )}
        {activeTab === 'pickup' && pickupView==='completed' && completedPickupOrders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No completed pickup orders</h3>
            <p className="text-gray-500">Completed pickup orders will show here.</p>
          </div>
        )}
      </div>

      {/* Remittance Confirmation Modal */}
      {showRemittanceModal && selectedDelivery && (
        <RemittanceConfirmationModal
          delivery={selectedDelivery}
          onClose={() => setShowRemittanceModal(false)}
          onConfirm={() => {
            setShowRemittanceModal(false);
            setSelectedDelivery(null);
            // Refresh the page or update local state
            window.location.reload();
          }}
        />
      )}

      {/* Delivery Details Modal */}
      {showDeliveryModal && selectedDelivery && (
        <DeliveryDetailsModal
          delivery={selectedDelivery}
          onClose={() => setShowDeliveryModal(false)}
          onStatusUpdate={handleStatusUpdate}
          getRiderName={getRiderName}
        />
      )}
    </div>
  );
};

export default DeliveryManagementDashboard;
